import { createContext, useContext, useState, useEffect, useRef } from "react";
import { useAuth } from "./AuthContext";
import { getClients } from "../utils/clients";
import { getInvoices } from "../utils/invoices";
import { getEstimates } from "../utils/estimates";
import { getJobs } from "../utils/jobs";

const AppDataContext = createContext(null);

// Caches clients/invoices/estimates/jobs across page navigation so switching
// between Invoices, Estimates, Clients, and search doesn't re-read the same
// collections from Firestore every time. Each loadX() returns cached data
// instantly if present, or fetches once and caches it. Call invalidateX()
// after any create/update/delete so the next load gets fresh data.
export function AppDataProvider({ children }) {
  const { user } = useAuth();
  const [clients, setClients] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [estimates, setEstimates] = useState(null);
  const [jobs, setJobs] = useState(null);
  const inFlightRef = useRef({});

  // Clear the cache whenever the logged-in user changes (login/logout/switch)
  useEffect(() => {
    setClients(null);
    setInvoices(null);
    setEstimates(null);
    setJobs(null);
    inFlightRef.current = {};
  }, [user?.uid]);

  function makeLoader(key, currentValue, setValue, fetchFn) {
    return async (force = false) => {
      if (!user) return [];
      if (currentValue && !force) return currentValue;
      if (inFlightRef.current[key]) return inFlightRef.current[key];
      const promise = fetchFn(user.uid).then((data) => {
        setValue(data);
        inFlightRef.current[key] = null;
        return data;
      }).catch((err) => {
        inFlightRef.current[key] = null;
        throw err;
      });
      inFlightRef.current[key] = promise;
      return promise;
    };
  }

  const loadClients = makeLoader("clients", clients, setClients, getClients);
  const loadInvoices = makeLoader("invoices", invoices, setInvoices, getInvoices);
  const loadEstimates = makeLoader("estimates", estimates, setEstimates, getEstimates);
  const loadJobs = makeLoader("jobs", jobs, setJobs, getJobs);

  const value = {
    clients,
    invoices,
    estimates,
    jobs,
    loadClients,
    loadInvoices,
    loadEstimates,
    loadJobs,
    invalidateClients: () => setClients(null),
    invalidateInvoices: () => setInvoices(null),
    invalidateEstimates: () => setEstimates(null),
    invalidateJobs: () => setJobs(null),
  };

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error("useAppData must be used within AppDataProvider");
  return ctx;
}
